import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file.');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
});

// Enhanced connection checking with comprehensive error handling
export async function checkSupabaseConnection() {
  try {
    // Test basic connectivity and authentication state
    const { data, error } = await supabase?.auth?.getSession();
    
    if (error) {
      console.warn('Supabase auth check warning:', error);
      // Auth errors don't necessarily mean no connection
      return {
        isConnected: true,
        error: null,
        warning: 'Authentication service available but session check failed'
      };
    }

    // Test database connectivity with a simple query
    try {
      const { error: dbError } = await supabase?.from('user_profiles')?.select('id')?.limit(1);

      if (dbError) {
        console.warn('Database connectivity check failed:', dbError);
        return {
          isConnected: false,
          error: {
            message: 'Database connection failed',
            details: dbError?.message
          }
        };
      }

      return {
        isConnected: true,
        error: null,
        lastCheck: new Date()?.toISOString()
      };
    } catch (dbError) {
      console.error('Database connection test failed:', dbError);
      return {
        isConnected: false,
        error: {
          message: 'Database connectivity test failed',
          details: dbError?.message
        }
      };
    }
  } catch (connectionError) {
    console.error('Supabase connection check failed:', connectionError);
    
    // Determine error type for better handling
    const isNetworkError = connectionError?.message?.includes('Failed to fetch') || 
                          connectionError?.message?.includes('NetworkError') ||
                          connectionError?.name === 'TypeError';

    return {
      isConnected: false,
      error: {
        message: isNetworkError ? 'Network connection error' : 'Supabase connection failed',
        details: connectionError?.message,
        isNetworkError
      }
    };
  }
}

// Enhanced network monitoring with proper event handling
export function createNetworkMonitor(callback) {
  if (!callback || typeof callback !== 'function') {
    console.warn('createNetworkMonitor: Invalid callback provided');
    return () => {};
  }

  // Initial network status
  let isOnline = navigator?.onLine ?? true;
  
  // Notify initial status
  try {
    callback({ isOnline, timestamp: new Date()?.toISOString() });
  } catch (error) {
    console.error('Network monitor callback failed:', error);
  }

  // Network event handlers with error protection
  const handleOnline = () => {
    isOnline = true;
    try {
      callback({ 
        isOnline: true, 
        timestamp: new Date()?.toISOString(),
        event: 'online'
      });
    } catch (error) {
      console.error('Online callback failed:', error);
    }
  };

  const handleOffline = () => {
    isOnline = false;
    try {
      callback({ 
        isOnline: false, 
        timestamp: new Date()?.toISOString(),
        event: 'offline'
      });
    } catch (error) {
      console.error('Offline callback failed:', error);
    }
  };

  // Add event listeners with error handling
  try {
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
  } catch (error) {
    console.error('Failed to add network event listeners:', error);
  }

  // Periodic connectivity check for more reliable monitoring
  const connectivityInterval = setInterval(() => {
    const currentOnlineStatus = navigator?.onLine ?? true;
    if (currentOnlineStatus !== isOnline) {
      isOnline = currentOnlineStatus;
      try {
        callback({ 
          isOnline, 
          timestamp: new Date()?.toISOString(),
          event: 'status_change'
        });
      } catch (error) {
        console.error('Periodic connectivity callback failed:', error);
      }
    }
  }, 30000); // Check every 30 seconds

  // Cleanup function
  return () => {
    try {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(connectivityInterval);
    } catch (error) {
      console.error('Network monitor cleanup failed:', error);
    }
  };
}

// Additional helper for testing database health
export async function testDatabaseHealth() {
  try {
    const startTime = performance.now();
    
    // Test a simple query
    const { error } = await supabase?.from('user_profiles')?.select('count')?.limit(1);
    
    const responseTime = performance.now() - startTime;
    
    if (error) {
      return {
        healthy: false,
        error: error?.message,
        responseTime: responseTime
      };
    }

    return {
      healthy: true,
      responseTime: responseTime,
      timestamp: new Date()?.toISOString()
    };
  } catch (error) {
    return {
      healthy: false,
      error: error?.message,
      timestamp: new Date()?.toISOString()
    };
  }
}

// FIXED: Removed conflicting default export to prevent import confusion
// Only use named exports for consistency